import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { newsList } from '@/mocks/newsData';

function renderContent(content: string) {
  // Parse markdown-like content into sections
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let key = 0;
  let inTable = false;
  let tableRows: string[][] = [];
  let tableHeaders: string[] = [];

  const flushTable = () => {
    if (tableRows.length === 0) return;
    elements.push(
      <div key={`table-${key++}`} className="overflow-x-auto my-6">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-firmai-border">
              {tableHeaders.map((h, i) => (
                <th key={i} className="text-left py-2 px-3 text-firmai-green font-semibold text-xs uppercase tracking-wider">{h.trim()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri} className="border-b border-firmai-border/40 hover:bg-white/5 transition-colors">
                {row.map((cell, ci) => (
                  <td key={ci} className="py-2.5 px-3 text-firmai-text text-sm">{cell.trim()}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
    tableHeaders = [];
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table rows
    if (line.startsWith('|')) {
      const cells = line.split('|').filter((_, idx) => idx !== 0 && idx !== line.split('|').length - 1);
      if (line.includes('---')) {
        // separator row, skip
        continue;
      }
      if (!inTable) {
        inTable = true;
        tableHeaders = cells;
      } else {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      flushTable();
    }

    if (!line.trim()) {
      elements.push(<div key={key++} className="h-3" />);
      continue;
    }

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={key++} className="text-xl font-bold text-white mt-8 mb-4 pb-2 border-b border-firmai-border/50">
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="text-base font-bold text-firmai-green mt-6 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
    } else if (line.startsWith('- ')) {
      // collect all consecutive list items
      const listItems: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push(lines[i].replace('- ', ''));
        i++;
      }
      i--; // back one since loop will increment
      elements.push(
        <ul key={key++} className="space-y-2 my-4 ml-2">
          {listItems.map((item, idx) => {
            const parts = item.split('**');
            return (
              <li key={idx} className="flex items-start gap-2 text-firmai-text text-sm leading-relaxed">
                <span className="w-4 h-4 flex items-center justify-center text-firmai-green shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-line text-xs" />
                </span>
                <span>
                  {parts.map((p, pi) => pi % 2 === 1 ? <strong key={pi} className="text-white font-semibold">{p}</strong> : p)}
                </span>
              </li>
            );
          })}
        </ul>
      );
    } else if (/^\d+\.\s/.test(line)) {
      // numbered list
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      i--;
      elements.push(
        <ol key={key++} className="space-y-2 my-4 ml-2">
          {listItems.map((item, idx) => {
            const parts = item.split('**');
            return (
              <li key={idx} className="flex items-start gap-3 text-firmai-text text-sm leading-relaxed">
                <span className="w-5 h-5 flex items-center justify-center bg-firmai-green/10 text-firmai-green rounded-full text-xs font-bold shrink-0 mt-0.5">
                  {idx + 1}
                </span>
                <span>
                  {parts.map((p, pi) => pi % 2 === 1 ? <strong key={pi} className="text-white font-semibold">{p}</strong> : p)}
                </span>
              </li>
            );
          })}
        </ol>
      );
    } else {
      // normal paragraph, handle **bold**
      const parts = line.split('**');
      elements.push(
        <p key={key++} className="text-firmai-text text-sm leading-relaxed my-2">
          {parts.map((p, pi) => pi % 2 === 1 ? <strong key={pi} className="text-white font-semibold">{p}</strong> : p)}
        </p>
      );
    }
  }

  if (inTable) flushTable();
  return elements;
}

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const topRef = useRef<HTMLDivElement>(null);

  const news = newsList.find((n) => n.id === Number(id));

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [id]);

  if (!news) {
    return (
      <div className="min-h-screen bg-firmai-dark">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-40 text-center">
          <span className="w-16 h-16 flex items-center justify-center text-firmai-muted mb-4">
            <i className="ri-file-unknow-line text-5xl" />
          </span>
          <p className="text-firmai-muted text-lg mb-6">文章不存在或已下线</p>
          <button
            onClick={() => navigate('/news')}
            className="px-6 py-2.5 bg-firmai-green text-firmai-dark font-semibold rounded-md cursor-pointer whitespace-nowrap"
          >
            返回政策资讯
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedNews = newsList.filter((n) => n.id !== news.id && n.category === news.category).slice(0, 3);
  const otherNews = newsList.filter((n) => n.id !== news.id && n.category !== news.category).slice(0, 3 - relatedNews.length);
  const sideNews = [...relatedNews, ...otherNews];

  return (
    <div className="min-h-screen bg-firmai-dark" ref={topRef}>
      <Navbar />
      <main className="pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4">
          <nav className="flex items-center gap-2 text-xs text-firmai-muted">
            <button onClick={() => navigate('/')} className="hover:text-firmai-green transition-colors cursor-pointer whitespace-nowrap">首页</button>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line" /></span>
            <button onClick={() => navigate('/news')} className="hover:text-firmai-green transition-colors cursor-pointer whitespace-nowrap">政策资讯</button>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line" /></span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${news.categoryColor}`}>{news.category}</span>
          </nav>
        </div>

        <div className="w-full px-4 md:px-8 lg:px-12 pb-20">
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Main Article */}
            <article className="w-full lg:flex-1 min-w-0">
              {/* Cover Image */}
              <div className="relative h-56 md:h-80 rounded-xl overflow-hidden mb-6">
                <img
                  src={news.image}
                  alt={news.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-firmai-dark/70 via-transparent to-transparent" />
                {news.hot && (
                  <span className="absolute top-4 right-4 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                    热点
                  </span>
                )}
              </div>

              {/* Article Header */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${news.categoryColor}`}>
                    {news.category}
                  </span>
                  <span className="text-firmai-muted text-xs">{news.date}</span>
                  <span className="text-firmai-muted text-xs">·</span>
                  <span className="flex items-center gap-1 text-firmai-muted text-xs">
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line" /></span>
                    预计阅读 {news.readTime}
                  </span>
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-white leading-snug">
                  {news.title}
                </h1>
                <p className="text-firmai-muted text-sm mt-3 leading-relaxed border-l-2 border-firmai-green/40 pl-3">
                  {news.summary}
                </p>
              </div>

              {/* Article Content */}
              <div className="bg-firmai-card border border-firmai-border rounded-xl p-6 md:p-8">
                {renderContent(news.content)}
              </div>

              {/* Action Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6 pt-6 border-t border-firmai-border">
                <button
                  onClick={() => navigate('/news')}
                  className="inline-flex items-center gap-2 text-firmai-muted hover:text-firmai-green transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
                  返回政策资讯列表
                </button>
                <div className="flex items-center gap-3">
                  <span className="text-firmai-muted text-xs">分享：</span>
                  {[
                    { icon: 'ri-wechat-fill', label: '微信' },
                    { icon: 'ri-wechat-2-fill', label: '朋友圈' },
                    { icon: 'ri-link', label: '复制链接' },
                  ].map((s) => (
                    <button
                      key={s.label}
                      title={s.label}
                      className="w-8 h-8 flex items-center justify-center bg-firmai-card border border-firmai-border rounded-full text-firmai-muted hover:text-firmai-green hover:border-firmai-green/30 transition-all duration-200 cursor-pointer"
                    >
                      <i className={s.icon} />
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 bg-gradient-to-r from-firmai-green/10 to-transparent border border-firmai-green/20 rounded-xl p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-white font-semibold text-base">需要专业合规服务？</p>
                    <p className="text-firmai-muted text-sm mt-1">寻御科技专业团队为您提供一对一政策解读与合规方案</p>
                  </div>
                  <button
                    onClick={() => navigate('/contact')}
                    className="px-6 py-2.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0"
                  >
                    免费咨询
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 shrink-0 space-y-5">
              {/* Contact Card */}
              <div className="bg-firmai-card border border-firmai-border rounded-xl p-5">
                <h3 className="text-white font-semibold text-sm mb-3">专家在线咨询</h3>
                <p className="text-firmai-muted text-xs leading-relaxed mb-4">
                  对本文政策内容有疑问？寻御科技专业顾问24小时内回复您
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-xs text-firmai-text">
                    <span className="w-4 h-4 flex items-center justify-center text-firmai-green"><i className="ri-phone-line" /></span>
                    18916035855
                  </div>
                  <div className="flex items-center gap-2 text-xs text-firmai-text">
                    <span className="w-4 h-4 flex items-center justify-center text-firmai-green"><i className="ri-mail-line" /></span>
                    info@xunyutech.cn
                  </div>
                </div>
                <button
                  onClick={() => navigate('/contact')}
                  className="w-full py-2 bg-firmai-green text-firmai-dark text-sm font-semibold rounded-md hover:bg-firmai-green/90 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  立即咨询
                </button>
              </div>

              {/* Related Articles */}
              {sideNews.length > 0 && (
                <div className="bg-firmai-card border border-firmai-border rounded-xl p-5">
                  <h3 className="text-white font-semibold text-sm mb-4">相关资讯</h3>
                  <div className="space-y-4">
                    {sideNews.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => navigate(`/news/${item.id}`)}
                        className="flex gap-3 cursor-pointer group"
                      >
                        <div className="w-16 h-12 shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-firmai-text text-xs leading-snug line-clamp-2 group-hover:text-firmai-green transition-colors duration-200">
                            {item.title}
                          </p>
                          <p className="text-firmai-muted text-xs mt-1">{item.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Filter */}
              <div className="bg-firmai-card border border-firmai-border rounded-xl p-5">
                <h3 className="text-white font-semibold text-sm mb-3">分类浏览</h3>
                <div className="flex flex-wrap gap-2">
                  {['工信部政策', 'CCER动态', 'CBAM新规', '工业AI', '双碳科普'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => navigate('/news')}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-firmai-dark border border-firmai-border text-firmai-text hover:border-firmai-green/40 hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}